document.addEventListener('DOMContentLoaded', function () {

    // Cart operation queue to prevent conflicts
    class CartOperationQueue {
        constructor() {
            this.queue = [];
            this.isProcessing = false;
        }

        async add(operation) {
            return new Promise((resolve, reject) => {
                this.queue.push({
                    operation,
                    resolve,
                    reject,
                    retries: 0,
                    maxRetries: 3
                });
                this.process();
            });
        }

        async process() {
            if (this.isProcessing || this.queue.length === 0) {
                return;
            }

            this.isProcessing = true;
            const { operation, resolve, reject, retries, maxRetries } = this.queue.shift();

            try {
                const result = await operation();
                resolve(result);
            } catch (error) {
                if (retries < maxRetries) {
                    // Retry with exponential backoff
                    const delay = Math.pow(2, retries) * 500; // 500ms, 1s, 2s
                    console.log(`Cart operation failed, retrying in ${delay}ms... (attempt ${retries + 1}/${maxRetries})`);

                    setTimeout(() => {
                        this.queue.unshift({
                            operation,
                            resolve,
                            reject,
                            retries: retries + 1,
                            maxRetries
                        });
                        this.isProcessing = false;
                        this.process();
                    }, delay);
                } else {
                    reject(error);
                }
            }

            this.isProcessing = false;
            // Process next operation if any
            setTimeout(() => this.process(), 100);
        }
    }

    // Global cart queue instance
    const cartQueue = new CartOperationQueue();

    // Cart state management to prevent race conditions
    let isCartFetching = false;
    let cartFetchQueue = [];

    // Consolidated cart fetch function to prevent race conditions
    async function fetchCartSafely() {
        return new Promise((resolve, reject) => {
            cartFetchQueue.push({ resolve, reject });

            if (isCartFetching) {
                console.log('Cart fetch already in progress, queuing request...');
                return;
            }

            processCartFetchQueue();
        });
    }

    async function processCartFetchQueue() {
        if (isCartFetching || cartFetchQueue.length === 0) return;

        isCartFetching = true;
        console.log('Fetching cart for', cartFetchQueue.length, 'queued requests...');

        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();

            // Resolve all queued requests with the same cart data
            const queueCopy = [...cartFetchQueue];
            cartFetchQueue = [];

            queueCopy.forEach(({ resolve }) => resolve(cart));

        } catch (error) {
            console.error('Cart fetch failed:', error);

            // Reject all queued requests
            const queueCopy = [...cartFetchQueue];
            cartFetchQueue = [];

            queueCopy.forEach(({ reject }) => reject(error));
        } finally {
            isCartFetching = false;

            // Process any new requests that came in
            if (cartFetchQueue.length > 0) {
                setTimeout(processCartFetchQueue, 100);
            }
        }

        // Set up periodic cart maintenance (runs every hour)
        setInterval(() => {
            cleanupExpiredCartData();
            updateCartTimestamp();
        }, 3600000); // 1 hour = 3600000ms
    }

    // Additional cart protection - intercept dangerous cart operations
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
        const url = args[0];
        const options = args[1];

        // Check for potentially dangerous cart operations
        if (typeof url === 'string') {
            // Handle cart/clear requests - only allow if from checkout completion
            if (url.includes('/cart/clear')) {
                const isCheckoutCompletion = url.includes('checkout') || document.referrer.includes('checkout');
                if (isCheckoutCompletion) {
                    console.log('✅ Allowing cart/clear from checkout completion');
                    // Clear localStorage when checkout is completed
                    localStorage.removeItem('cart-persistent-data');
                    console.log('🗑️ Cleared persistent cart data due to checkout completion');
                } else {
                    console.warn('🚫 Blocking cart/clear request that could reset cart');
                    // Return a fake successful response to prevent errors
                    return Promise.resolve(new Response('{"success": false, "message": "Cart clear blocked"}', {
                        status: 200,
                        headers: { 'Content-Type': 'application/json' }
                    }));
                }
            }

            // Monitor bulk cart update requests and log them
            if (url.includes('/cart/update') && options?.method === 'POST') {
                console.log('🔍 Monitoring cart/update request:', { url, options });

                // Check if it's a bulk update that could replace cart contents
                if (options?.body) {
                    const formData = options.body;
                    if (formData instanceof FormData) {
                        const updates = formData.getAll('updates[]');
                        if (updates.length > 0) {
                            console.warn('⚠️ Bulk cart update detected with updates[]:', updates);
                            console.log('This could potentially replace cart contents - monitoring...');
                        }
                    }
                }
            }

            // Log all cart-related operations for debugging
            if (url.includes('/cart/') && !url.includes('/cart.js')) {
                console.log('📦 Cart operation:', { url, method: options?.method || 'GET' });
            }
        }

        // Continue with the original fetch and monitor for cart changes
        const response = await originalFetch.apply(this, args);

        // If this was a successful cart operation, update localStorage
        if (typeof url === 'string' && response.ok && (
            url.includes('/cart/add') ||
            url.includes('/cart/update') ||
            url.includes('/cart/change') ||
            url.includes('/cart/clear')
        )) {
            // Small delay to let the cart API update, then save to localStorage
            setTimeout(async () => {
                try {
                    const cartResponse = await fetch('/cart.js');
                    if (cartResponse.ok) {
                        const cart = await cartResponse.json();
                        saveCartToLocalStorage(cart);
                        console.log('🔄 Auto-saved cart to localStorage after API operation');
                    }
                } catch (error) {
                    console.log('Failed to auto-save cart after operation:', error);
                }
            }, 200);
        }

        return response;
    };

    // Check for cart state persistence on page load
    checkCartStatePersistence();

    initCartEnhancements();

    // Enhanced cart persistence system using localStorage for long-term storage
    function checkCartStatePersistence() {
        console.log('Checking enhanced cart state persistence on page load...');

        // Check localStorage for persistent cart data (14 day retention)
        const persistentCartData = localStorage.getItem('cart-persistent-data');

        if (persistentCartData) {
            try {
                const { count, items, timestamp, lastUpdated } = JSON.parse(persistentCartData);

                // Use data if less than 30 days old (30 * 24 * 60 * 60 * 1000 = 2592000000ms)
                const thirtyDaysAgo = Date.now() - 2592000000;

                if (timestamp > thirtyDaysAgo) {
                    console.log('Found persistent cart data:', { count, itemCount: items?.length || 0, age: Math.round((Date.now() - timestamp) / (1000 * 60 * 60)) + ' hours' });

                    if (count > 0) {
                        // Update cart count display immediately
                        updateCartCount(count);

                        // Validate and sync with server (essential for accuracy)
                        setTimeout(() => {
                            validateAndSyncCartState(items, count);
                        }, 100);
                    }
                } else {
                    console.log('Persistent cart data expired (>30 days), clearing...');
                    localStorage.removeItem('cart-persistent-data');
                }
            } catch (error) {
                console.log('Error parsing persistent cart data:', error);
                localStorage.removeItem('cart-persistent-data');
            }
        }

        // Fallback: Check sessionStorage for cart count (for compatibility)
        const sessionStorageCount = sessionStorage.getItem('cart-count');
        if (sessionStorageCount && !persistentCartData) {
            try {
                const { value, timestamp } = JSON.parse(sessionStorageCount);
                const count = parseInt(value, 10);

                if (count > 0) {
                    console.log('Found session cart count as fallback:', count, 'items');
                    updateCartCount(count);
                }
            } catch (error) {
                console.log('Error parsing session cart count:', error);
            }
        }

        // ALWAYS validate with server on page load for accuracy
        setTimeout(() => {
            console.log('Performing server cart validation on page load...');
            validateCartStateIntegrity();
        }, 200);

        // Handle cart drawer state persistence (still use sessionStorage for UI state)
        const shouldShowCartAfterNavigation = sessionStorage.getItem('show-cart-after-navigation');
        const isCartPage = window.location.pathname === '/cart' || window.location.pathname.includes('/cart');

        if (shouldShowCartAfterNavigation === 'true' && !isCartPage) {
            console.log('Showing cart drawer after page navigation...');
            sessionStorage.removeItem('show-cart-after-navigation');

            setTimeout(() => {
                const drawer = document.getElementById('custom-cart-drawer');
                if (drawer) {
                    drawer.classList.add('active');
                    refreshCartDrawer();
                }
            }, 500);
        } else if (shouldShowCartAfterNavigation === 'true' && isCartPage) {
            console.log('On cart page - clearing cart drawer flag');
            sessionStorage.removeItem('show-cart-after-navigation');
        }
    }

    // Enhanced cart state validation and syncing with localStorage
    function validateAndSyncCartState(expectedItems, expectedCount) {
        console.log('Validating and syncing cart state with server...', { expectedItems: expectedItems?.length || 0, expectedCount });

        fetchCartSafely()
            .then(cart => {
                const actualCount = cart.item_count || 0;
                const actualItems = cart.items || [];

                console.log('Cart sync - Expected:', expectedCount, 'Actual:', actualCount);

                // Update displays with actual server data
                updateCartCount(actualCount);

                // Update localStorage with current server state
                saveCartToLocalStorage(cart);

                // Check for significant discrepancies
                if (Math.abs(actualCount - expectedCount) > 0) {
                    console.log('Cart sync detected differences - updated with server data');
                }

                // Show cart bubble if items exist
                if (actualCount > 0) {
                    const cartBubbles = document.querySelectorAll('.cart-bubble');
                    cartBubbles.forEach(bubble => {
                        bubble.style.display = 'block';
                        bubble.style.animation = 'cart-bubble-pulse 0.6s ease-in-out';
                    });
                }
            })
            .catch(error => {
                console.log('Cart sync failed, but this is not critical:', error);
            });
    }

    // Validates cart state integrity across page navigation
    function validateCartStateIntegrity() {
        console.log('Validating cart state integrity...');

        fetchCartSafely()
            .then(cart => {
                const actualCount = cart.item_count || 0;
                const sessionCount = sessionStorage.getItem('cart-count');

                console.log('Cart validation - Actual count:', actualCount);

                if (sessionCount) {
                    try {
                        const { value } = JSON.parse(sessionCount);
                        const storedCount = parseInt(value, 10);

                        console.log('Cart validation - Stored count:', storedCount);

                        // If counts don't match, update with actual count
                        if (storedCount !== actualCount) {
                            console.log('Cart count mismatch detected, syncing with server...');
                            updateCartCount(actualCount);
                            // Save updated cart data to localStorage
                            saveCartToLocalStorage(cart);
                        }
                    } catch (error) {
                        console.log('Error parsing stored cart count, updating with actual count');
                        updateCartCount(actualCount);
                        // Save cart data to localStorage
                        saveCartToLocalStorage(cart);
                    }
                } else {
                    // No stored count, update with actual
                    updateCartCount(actualCount);
                    // Save cart data to localStorage
                    saveCartToLocalStorage(cart);
                }

                // If cart has items but user was browsing, show a subtle indication
                if (actualCount > 0) {
                    const cartBubbles = document.querySelectorAll('.cart-bubble');
                    cartBubbles.forEach(bubble => {
                        bubble.style.display = 'block';
                        // Add a subtle animation to draw attention
                        bubble.style.animation = 'none';
                        bubble.offsetHeight; // Trigger reflow
                        bubble.style.animation = 'cart-bubble-pulse 0.6s ease-in-out';
                    });
                }
            })
            .catch(error => {
                console.log('Cart validation failed, but this is not critical:', error);
            });
    }

    function initCartEnhancements() {
        addLoadingStates();

        enhanceQuantitySelectors();

        addOverlayClose();

        addKeyboardNavigation();

        initCustomCartDrawer();

        setTimeout(function () {
            interceptCartLinks();
        }, 100);
    }

    function addLoadingStates() {
        const cartForms = document.querySelectorAll('form[action*="/cart"]');

        cartForms.forEach(function (form) {
            form.addEventListener('submit', function () {
                const submitButton = form.querySelector('button[type="submit"]');
                if (submitButton && submitButton instanceof HTMLButtonElement) {
                    submitButton.classList.add('cart-loading');
                    submitButton.disabled = true;
                }
            });
        });

        const quantityButtons = document.querySelectorAll('.quantity-selector__button');

        quantityButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                const form = button.closest('form');
                if (form) {
                    form.classList.add('cart-loading');
                }
            });
        });
    }

    function enhanceQuantitySelectors() {
        const quantityInputs = document.querySelectorAll('.quantity-selector__input');

        quantityInputs.forEach(function (input) {
            input.addEventListener('input', function (e) {
                if (e.target && e.target instanceof HTMLInputElement) {
                    let value = parseInt(e.target.value);
                    if (isNaN(value) || value < 0) {
                        e.target.value = '1';
                    }
                }
            });

            input.addEventListener('keypress', function (e) {
                if (e instanceof KeyboardEvent && e.key === 'Enter') {
                    e.preventDefault();
                    const form = input.closest('form');
                    if (form) {
                        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                        form.dispatchEvent(submitEvent);
                    }
                }
            });
        });
    }

    function addOverlayClose() {
        const cartDrawer = document.querySelector('cart-drawer-component');

        if (cartDrawer) {
            const dialog = cartDrawer.querySelector('dialog');

            if (dialog) {
                dialog.addEventListener('click', function (e) {
                    if (e.target === dialog && typeof cartDrawer.close === 'function') {
                        cartDrawer.close();
                    }
                });
            }
        }
    }

    function addKeyboardNavigation() {
        document.addEventListener('keydown', function (e) {
            if (e instanceof KeyboardEvent && e.key === 'Escape') {
                const cartDrawer = document.querySelector('cart-drawer-component');
                if (cartDrawer && cartDrawer.hasAttribute('open') && typeof cartDrawer.close === 'function') {
                    cartDrawer.close();
                }

                const customDrawer = document.getElementById('custom-cart-drawer');
                if (customDrawer && customDrawer.classList.contains('active')) {
                    closeCustomCartDrawer();
                }
            }
        });
    }

    function initCustomCartDrawer() {
        const drawerHTML = `
      <div id="custom-cart-drawer" class="custom-cart-drawer">
        <div class="custom-cart-drawer__overlay"></div>
        <div class="custom-cart-drawer__content">
          <div class="custom-cart-drawer__header">
            <h3 class="custom-cart-drawer__title">Added to Cart</h3>
            <button class="custom-cart-drawer__close" aria-label="Close">×</button>
          </div>
          <div class="custom-cart-drawer__body">
            <div class="custom-cart-drawer__items">
            </div>
            <div class="custom-cart-drawer__summary">
              <div class="custom-cart-drawer__total">
                <span class="custom-cart-drawer__total-label">Total:</span>
                <span class="custom-cart-drawer__total-price">$0.00</span>
              </div>
            </div>
          </div>
          <div class="custom-cart-drawer__footer">
            <button class="custom-cart-drawer__continue">Continue Shopping</button>
            <button class="custom-cart-drawer__view-cart">View Cart</button>
          </div>
        </div>
      </div>
            `;

        document.addEventListener('DOMContentLoaded', function () {
            const sizeDropdowns = document.querySelectorAll('.size-dropdown-button');
            sizeDropdowns.forEach(dropdown => {
                const textElement = dropdown.querySelector('.size-selected-text');
                if (textElement) {
                    textElement.setAttribute('data-default', textElement.textContent);
                }
            });
        });

        document.body.insertAdjacentHTML('beforeend', drawerHTML);

        const drawer = document.getElementById('custom-cart-drawer');
        const overlay = drawer.querySelector('.custom-cart-drawer__overlay');
        const closeBtn = drawer.querySelector('.custom-cart-drawer__close');
        const continueBtn = drawer.querySelector('.custom-cart-drawer__continue');
        const viewCartBtn = drawer.querySelector('.custom-cart-drawer__view-cart');

        if (overlay) {
            overlay.addEventListener('click', closeCustomCartDrawer);
        }

        if (closeBtn) {
            closeBtn.addEventListener('click', closeCustomCartDrawer);
        }

        if (continueBtn) {
            continueBtn.addEventListener('click', closeCustomCartDrawer);
        }

        if (viewCartBtn) {
            viewCartBtn.addEventListener('click', function () {
                // Close drawer immediately and clear any navigation flags
                closeCustomCartDrawer();
                sessionStorage.removeItem('show-cart-after-navigation');

                // Navigate to cart page
                window.location.href = '/cart';
            });
        }

        // Intercept add to cart forms
        const addToCartForms = document.querySelectorAll('form[action*="/cart/add"]');

        addToCartForms.forEach(function (form) {
            form.addEventListener('submit', async function (e) {
                e.preventDefault();

                // CRITICAL: Run inventory validation FIRST before any cart operations
                if (window.InventoryValidator) {
                    // Clear cache to get absolutely fresh cart data
                    window.InventoryValidator.clearCartCache();

                    // Run comprehensive inventory validation
                    const isValid = await window.InventoryValidator.validateProductForm(form);
                    if (!isValid) {
                        console.log('🚫 Inventory validation failed - blocking cart addition');

                        // CRITICAL: Force cleanup button state after validation failure
                        // The button might be stuck in a "submitting" or "loading" state
                        const submitButton = form.querySelector('button[type="submit"]');
                        if (submitButton) {
                            submitButton.classList.remove('cart-loading');
                            submitButton.disabled = false;
                            submitButton.style.pointerEvents = '';
                            console.log('🔧 Force cleaned button loading state');
                        }

                        setTimeout(() => {
                            if (window.InventoryValidator) {
                                window.InventoryValidator.updateInventoryWarnings();
                                window.InventoryValidator.refreshDropdownState();
                                console.log('🔄 Refreshed button state after validation failure');
                            }
                        }, 100);

                        return; // Stop execution - inventory validator shows error modal
                    }
                    console.log('✅ Inventory validation passed - proceeding with cart addition');
                }

                // Proceed with cart addition only if validation passed
                await handleAddToCart(form);
            });
        });
    }

    function validateVariantSelection(form) {
        // Get the size dropdown text to see if a size is selected
        const sizeDropdown = form.querySelector('.size-dropdown-button');
        const sizeSelectedText = sizeDropdown ? sizeDropdown.querySelector('.size-selected-text') : null;
        const defaultPlaceholder = sizeSelectedText ? sizeSelectedText.getAttribute('data-default') || 'PRODUCT SIZE' : 'PRODUCT SIZE';

        // Check if we have a selected size (text changed from default)
        const hasSelectedSize = sizeSelectedText && sizeSelectedText.textContent !== defaultPlaceholder;

        console.log('Size validation:', {
            sizeDropdown: sizeDropdown,
            sizeSelectedText: sizeSelectedText ? sizeSelectedText.textContent : null,
            defaultPlaceholder: defaultPlaceholder,
            hasSelectedSize: hasSelectedSize
        });

        // If we have a size dropdown but no size is selected, show validation modal
        if (sizeDropdown && !hasSelectedSize) {
            // Check if any radio button is checked as backup
            const anyRadioChecked = form.querySelector('input[name="id"][type="radio"]:checked');

            // If no size is selected and no radio is checked, show validation
            if (!anyRadioChecked) {
                console.log('No size selected - showing validation modal');
                showVariantValidationModal();
                return false;
            }
        }

        // If we reach here, validation passed
        console.log('All validations passed!');
        return true;
    }

    function showVariantValidationModal() {
        // Create modal HTML matching your design style
        const modalHTML = `
            <div id="variant-validation-modal" class="variant-validation-modal">
                <div class="variant-validation-modal__overlay"></div>
                <div class="variant-validation-modal__content">
                    <div class="variant-validation-modal__message">
                        PLEASE SELECT SOME PRODUCT OPTIONS BEFORE<br>
                        ADDING THIS PRODUCT TO YOUR CART.
                    </div>
                    <button class="variant-validation-modal__close" aria-label="Close">×</button>
                </div>
            </div>
        `;

        // Remove existing modal if present
        const existingModal = document.getElementById('variant-validation-modal');
        if (existingModal) {
            existingModal.remove();
        }

        // Add modal to page
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        // Get modal elements
        const modal = document.getElementById('variant-validation-modal');
        const overlay = modal.querySelector('.variant-validation-modal__overlay');
        const closeBtn = modal.querySelector('.variant-validation-modal__close');

        // Show modal
        modal.classList.add('active');

        // Add event listeners to close modal
        const closeModal = function () {
            modal.classList.remove('active');
            setTimeout(() => {
                if (modal.parentNode) {
                    modal.parentNode.removeChild(modal);
                }
            }, 300);
        };

        overlay.addEventListener('click', closeModal);
        closeBtn.addEventListener('click', closeModal);

        // Close on escape key
        const handleEscape = function (e) {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);

        // Auto-close after 5 seconds
        setTimeout(closeModal, 5000);
    }

    async function handleAddToCart(form) {
        const submitButton = form.querySelector('button[type="submit"]');

        // VALIDATION: Check if required variants are selected
        if (!validateVariantSelection(form)) {
            // IMPORTANT: Clear loading state if validation fails
            if (submitButton && submitButton instanceof HTMLButtonElement) {
                submitButton.classList.remove('cart-loading');
                submitButton.disabled = false;
                submitButton.textContent = 'Add to Cart';
            }
            return; // Stop execution if validation fails
        }

        // Inventory validation now happens BEFORE this function is called

        const formData = new FormData(form);

        if (submitButton && submitButton instanceof HTMLButtonElement) {
            submitButton.classList.add('cart-loading');
            submitButton.disabled = true;
            submitButton.textContent = 'Adding...';
        }

        // Queue the add to cart operation
        cartQueue.add(async () => {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                if (response.status === 422) {
                    throw new Error('Item is no longer available');
                } else if (response.status === 429) {
                    throw new Error('Too many requests - please slow down');
                } else if (response.status >= 500) {
                    throw new Error('Server temporarily unavailable');
                } else {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
            }

            return response.json();
        })
            .then(function (product) {
                // Check if it's an error response from Shopify
                if (product.status && product.status !== 200) {
                    throw new Error(product.message || product.description || 'Unable to add item to cart');
                }

                // Check for specific inventory errors in the response
                if (product.message && product.message.includes('sold out')) {
                    throw new Error('This item is sold out');
                }

                if (product.message && product.message.includes('inventory')) {
                    throw new Error(product.message);
                }

                // Debug log to see what data we're getting
                console.log("Product added to cart:", product);

                // FIXED: Don't use showCustomCartDrawer which clears everything
                // Instead, show drawer and refresh with ALL items including the new one
                const drawer = document.getElementById('custom-cart-drawer');
                if (drawer) {
                    drawer.classList.add('active');
                    // Immediate refresh to show ALL cart items including the one just added
                    setTimeout(() => {
                        refreshCartDrawer();
                    }, 50); // Short delay to ensure cart is updated on server
                }

                // Update cart count in parallel (don't block drawer) - use lightweight approach
                updateCartCountFast();

                // Store cart state for potential page navigation
                sessionStorage.setItem('cart-recently-updated', JSON.stringify({
                    productId: product.product_id || product.id,
                    variantId: product.variant_id || product.id,
                    timestamp: Date.now()
                }));

                // Trigger inventory validation update
                if (window.InventoryValidator) {
                    setTimeout(() => {
                        document.dispatchEvent(new CustomEvent('cart:item-added'));
                    }, 100);
                }
            })
            .catch(function (error) {
                console.error('Error adding to cart after retries:', error);

                // Show better error message based on error type
                let errorMessage = 'Error adding to cart. Please try again.';

                if (error.message) {
                    if (error.message.includes('sold out') || error.message.includes('inventory')) {
                        errorMessage = error.message;
                    } else if (error.message.includes('no longer available')) {
                        errorMessage = 'Item is no longer available';
                    } else if (error.message.includes('Too many requests')) {
                        errorMessage = 'Please wait before adding more items';
                    } else if (error.message.includes('Server temporarily unavailable')) {
                        errorMessage = 'Server temporarily unavailable - please try again';
                    } else if (error.message !== 'Unable to add item to cart') {
                        errorMessage = error.message;
                    }
                }

                showCartMessage(errorMessage, 'error');
            })
            .finally(function () {
                if (submitButton && submitButton instanceof HTMLButtonElement) {
                    submitButton.classList.remove('cart-loading');
                    submitButton.disabled = false;
                    submitButton.textContent = 'Add to Cart';
                }
            });
    }

    // REMOVED: showCustomCartDrawer function was causing random cart resets
    // All cart display now uses refreshCartDrawer() for consistency

    // Cart refresh state management to prevent overlapping calls
    let isRefreshing = false;

    // FIXED: New function to refresh cart drawer without clearing/resetting
    function refreshCartDrawer() {
        const drawer = document.getElementById('custom-cart-drawer');
        const itemsContainer = drawer.querySelector('.custom-cart-drawer__items');
        const totalElement = drawer.querySelector('.custom-cart-drawer__total-price');

        if (!itemsContainer) return;

        // Prevent overlapping refreshes that could cause clearing conflicts
        if (isRefreshing) {
            console.log('Cart refresh already in progress, skipping duplicate call');
            return;
        }

        isRefreshing = true;
        console.log('Refreshing cart drawer with all items...');

        // Fetch current cart state safely (prevents race conditions)
        fetchCartSafely()
            .then(cart => {
                console.log('Cart fetched for refresh:', cart);

                // Save cart data to localStorage for persistence
                saveCartToLocalStorage(cart);

                // Create new content without clearing first (prevents flash of empty state)
                const newContent = document.createDocumentFragment();

                // If cart is empty, show empty message
                if (!cart.items || cart.items.length === 0) {
                    const emptyDiv = document.createElement('div');
                    emptyDiv.className = 'custom-cart-drawer__empty';
                    emptyDiv.textContent = 'Your cart is empty';
                    newContent.appendChild(emptyDiv);

                    // Update cart total
                    if (totalElement) {
                        totalElement.textContent = '$0.00';
                    }
                } else {
                    // Add each item to the drawer
                    cart.items.forEach((item, index) => {
                        const itemElement = document.createElement('div');
                        itemElement.className = 'custom-cart-drawer__item';

                        // Create item HTML - note: Shopify line items are 1-indexed
                        const lineNumber = index + 1;

                        // Count variants to determine spacing
                        const variantCount = item.variant_title && item.variant_title !== 'Default Title' ? 1 : 0;
                        const hasVariants = variantCount > 0;

                        itemElement.innerHTML = `
                         <div class="custom-cart-drawer__item-image">
                             <img src="${item.image}" alt="${item.product_title}" loading="lazy">
                         </div>
                         <div class="custom-cart-drawer__item-details ${hasVariants ? 'has-variants' : 'no-variants'}">
                             <h4 class="custom-cart-drawer__item-title">${item.product_title}</h4>
                             <p class="custom-cart-drawer__item-price">$${(item.price / 100).toFixed(2)}</p>
                             ${item.variant_title && item.variant_title !== 'Default Title' ?
                                `<p class="custom-cart-drawer__item-variant">${item.variant_title}</p>` : ''}
                             <p class="custom-cart-drawer__item-quantity">Qty: ${item.quantity}</p>
                             <button class="custom-cart-drawer__item-remove" data-variant-id="${item.variant_id}" data-line="${lineNumber}">Remove</button>
                         </div>
                     `;

                        // Add to new content
                        newContent.appendChild(itemElement);
                    });

                    // Update cart total with accurate data
                    if (totalElement) {
                        totalElement.textContent = `$${(cart.total_price / 100).toFixed(2)}`;
                    }
                }

                // Replace content atomically (prevents flicker and race conditions)
                itemsContainer.innerHTML = '';
                itemsContainer.appendChild(newContent);

                // Add event listeners to remove buttons with debounce protection
                const removeButtons = itemsContainer.querySelectorAll('.custom-cart-drawer__item-remove');
                removeButtons.forEach(button => {
                    // Remove any existing listeners to prevent duplicates
                    button.removeEventListener('click', handleRemoveItem);

                    // Add debounced event listener
                    let isRemoving = false;
                    button.addEventListener('click', async function (e) {
                        if (isRemoving) {
                            console.log('Already removing this item, ignoring click');
                            return;
                        }
                        isRemoving = true;

                        try {
                            await handleRemoveItem(e);
                        } finally {
                            // Reset after a delay to prevent spam clicking
                            setTimeout(() => {
                                isRemoving = false;
                            }, 1000);
                        }
                    });
                });

                console.log('Cart drawer refreshed successfully with', cart.items ? cart.items.length : 0, 'items');
            })
            .catch(error => {
                console.error('Error refreshing cart drawer:', error);
                itemsContainer.innerHTML = '<div class="custom-cart-drawer__empty">Unable to load cart</div>';
            })
            .finally(() => {
                // Reset refresh flag to allow future refreshes
                isRefreshing = false;
            });
    }

    function closeCustomCartDrawer() {
        const drawer = document.getElementById('custom-cart-drawer');
        if (drawer) {
            drawer.classList.remove('active');
            // No need to restore overflow since we don't hide it anymore

            // CRITICAL: Update inventory when cart drawer closes
            // This ensures add to cart buttons update to "sold out" if items were removed
            if (window.InventoryValidator) {
                setTimeout(() => {
                    document.dispatchEvent(new CustomEvent('cart:drawer-closed'));
                }, 100);
            }
        }
    }

    function interceptCartLinks() {
        // Find all cart links (both desktop and mobile)
        const cartLinks = document.querySelectorAll('a[href="/cart"], .bag-text[href="/cart"]');

        cartLinks.forEach(function (link) {
            // Only intercept if the link is specifically for cart (not cart/add etc.)
            if (link.getAttribute('href') === '/cart') {
                link.addEventListener('click', function (e) {
                    e.preventDefault();

                    // Always show the cart drawer, even if empty
                    const drawer = document.getElementById('custom-cart-drawer');
                    const itemsContainer = drawer.querySelector('.custom-cart-drawer__items');

                    // FIXED: Just show drawer and refresh - no need to duplicate cart logic
                    drawer.classList.add('active');
                    refreshCartDrawer();
                });
            }
        });
    }

    // Save complete cart data to localStorage for long-term persistence
    function saveCartToLocalStorage(cart) {
        try {
            const cartData = {
                count: cart.item_count || 0,
                items: cart.items || [],
                total_price: cart.total_price || 0,
                timestamp: Date.now(),
                lastUpdated: Date.now(),
                // Additional metadata for validation
                note: cart.note || '',
                attributes: cart.attributes || {},
                discounts: cart.cart_level_discount_applications || []
            };

            localStorage.setItem('cart-persistent-data', JSON.stringify(cartData));

            // Also maintain sessionStorage for compatibility
            sessionStorage.setItem('cart-count', JSON.stringify({
                value: String(cartData.count),
                timestamp: Date.now()
            }));

            console.log('💾 Cart saved to localStorage:', { count: cartData.count, items: cartData.items.length });
        } catch (error) {
            console.error('Failed to save cart to localStorage:', error);
        }
    }

    function updateCartCount(count) {
        const cartBubbles = document.querySelectorAll('.cart-bubble');
        cartBubbles.forEach(function (bubble) {
            bubble.textContent = count.toString();
            bubble.style.display = count > 0 ? 'block' : 'none';
        });

        // Update cart icon text
        const cartButtons = document.querySelectorAll('[aria-label*="cart"]');
        cartButtons.forEach(function (button) {
            if (button instanceof HTMLElement) {
                const currentLabel = button.getAttribute('aria-label') || '';
                const newLabel = currentLabel.replace(/\d+/, count.toString());
                button.setAttribute('aria-label', newLabel);
            }
        });

        // Update cart count in header
        const cartCountElements = document.querySelectorAll('.cart-count');
        if (cartCountElements) {
            cartCountElements.forEach(element => {
                element.textContent = count.toString();
            });
        }

        // CRITICAL: Persist cart count to sessionStorage for page navigation
        // This matches the theme's cart-icon.js persistence format
        sessionStorage.setItem('cart-count', JSON.stringify({
            value: String(count),
            timestamp: Date.now()
        }));

        console.log('Cart count updated and persisted:', count);
    }

    // Fast cart count update using safe fetch to prevent race conditions
    function updateCartCountFast() {
        fetchCartSafely()
            .then(cart => {
                updateCartCount(cart.item_count);
                // Also save to localStorage for persistence
                saveCartToLocalStorage(cart);
            })
            .catch(error => {
                console.log('Cart count update failed, but drawer still works');
            });
    }

    // Create cart item HTML instantly from product data
    function createCartItemHTML(product) {
        const price = product.price ? (product.price / 100).toFixed(2) : '0.00';
        const title = product.product_title || product.title || 'Product';
        const variant = product.variant_title && product.variant_title !== 'Default Title' ? product.variant_title : '';
        const quantity = product.quantity || 1;
        const imageUrl = product.image || '/assets/placeholder.png';

        return `
            <div class="custom-cart-drawer__item">
                <div class="custom-cart-drawer__item-image">
                    <img src="${imageUrl}" alt="${title}" loading="lazy">
                </div>
                <div class="custom-cart-drawer__item-details ${variant ? 'has-variants' : 'no-variants'}">
                    <div class="custom-cart-drawer__item-title">${title}</div>
                    ${variant ? `<div class="custom-cart-drawer__item-variant">${variant}</div>` : ''}
                    <div class="custom-cart-drawer__item-price">$${price}</div>
                </div>
                <div class="custom-cart-drawer__item-quantity">QTY: ${quantity}</div>
                <button class="custom-cart-drawer__item-remove" data-variant-id="${product.variant_id || product.id}">×</button>
            </div>
        `;
    }

    // Cart update success handler
    function handleCartUpdateSuccess() {
        // Remove loading states
        const loadingElements = document.querySelectorAll('.cart-loading');
        loadingElements.forEach(function (element) {
            element.classList.remove('cart-loading');
            if (element instanceof HTMLButtonElement) {
                element.disabled = false;
            }
        });

        // Show success message briefly
        showCartMessage('CART UPDATED', 'success');
    }

    // Cart update error handler
    function handleCartUpdateError(error) {
        // Remove loading states
        const loadingElements = document.querySelectorAll('.cart-loading');
        loadingElements.forEach(function (element) {
            element.classList.remove('cart-loading');
            if (element instanceof HTMLButtonElement) {
                element.disabled = false;
            }
        });

        // Show error message
        showCartMessage('ERROR UPDATING CART', 'error');
        console.error('Cart update error:', error);
    }

    async function handleRemoveItem(e) {
        e.preventDefault();
        const button = e.currentTarget;
        const variantId = button.getAttribute('data-variant-id');
        const line = button.getAttribute('data-line');
        const item = button.closest('.custom-cart-drawer__item');
        const drawer = document.getElementById('custom-cart-drawer');
        const totalElement = drawer.querySelector('.custom-cart-drawer__total-price');

        if (!item) return;

        // Prevent multiple clicks on the same button
        if (button.disabled) {
            console.log('Remove button already processing, ignoring click');
            return;
        }

        // Add loading state - only dim the button, not the whole item
        button.textContent = 'Removing...';
        button.disabled = true;
        button.style.opacity = '0.5';

        // Determine which data format to use
        let data;
        if (line) {
            // If we have a line number, use that (1-indexed in Shopify's API)
            data = { line: parseInt(line), quantity: 0 };
        } else if (variantId) {
            // If we have a variant ID, use the updates parameter
            data = { updates: { [variantId]: 0 } };
        } else {
            console.error('No line or variant ID found for item removal');
            button.style.opacity = '1';
            button.disabled = false;
            button.textContent = '×';
            return;
        }

        console.log('Queueing cart operation to remove item:', data);

        try {
            // Queue the cart operation to prevent conflicts
            const cart = await cartQueue.add(async () => {
                const response = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                });

                if (!response.ok) {
                    // More specific error handling
                    if (response.status === 422) {
                        throw new Error('Invalid cart operation - item may no longer exist');
                    } else if (response.status === 429) {
                        throw new Error('Too many requests - please slow down');
                    } else if (response.status >= 500) {
                        throw new Error('Server error - please try again');
                    } else {
                        throw new Error(`Request failed with status ${response.status}`);
                    }
                }

                return response.json();
            });

            console.log('Cart updated successfully:', cart);

            // Save updated cart to localStorage
            saveCartToLocalStorage(cart);

            // Update cart count
            updateCartCount(cart.item_count);

            // Update total price immediately
            if (totalElement) {
                totalElement.textContent = `$${(cart.total_price / 100).toFixed(2)}`;
            }

            // CRITICAL: Dispatch cart events for inventory validation
            document.dispatchEvent(new CustomEvent('cart:item-removed'));
            document.dispatchEvent(new CustomEvent('cart:updated'));

            // Show success message
            showCartMessage('Item removed', 'success');

            // Wait a moment before refreshing the drawer with ALL cart items
            setTimeout(() => {
                // FIXED: Don't call showCustomCartDrawer({}) which creates a blank instant item
                // Instead, directly fetch and update the cart contents
                refreshCartDrawer();
            }, 300);

        } catch (error) {
            console.error('Error removing item after retries:', error);

            // Better error messages based on error type
            let errorMessage = 'Error removing item';
            if (error.message.includes('no longer exist')) {
                errorMessage = 'Item already removed';
            } else if (error.message.includes('Too many requests')) {
                errorMessage = 'Please wait before removing more items';
            } else if (error.message.includes('Server error')) {
                errorMessage = 'Server temporarily unavailable';
            }

            showCartMessage(errorMessage, 'error');

            // Restore button appearance
            button.style.opacity = '1';
            button.disabled = false;
            button.textContent = '×';
        }
    }

    function showCartMessage(message, type) {
        type = type || 'success';

        // Get the cart drawer
        const drawer = document.getElementById('custom-cart-drawer');

        // If drawer doesn't exist, don't show message
        if (!drawer) return;

        // Get or create the dedicated message container ABOVE the cart items
        let messageContainer = drawer.querySelector('.custom-cart-drawer__message-container');
        if (!messageContainer) {
            messageContainer = document.createElement('div');
            messageContainer.className = 'custom-cart-drawer__message-container';

            // Insert BEFORE the cart body (above all items)
            const cartBody = drawer.querySelector('.custom-cart-drawer__body');
            if (cartBody) {
                cartBody.parentNode.insertBefore(messageContainer, cartBody);
            } else {
                return; // Can't find where to put the message
            }
        }

        // Remove any existing messages COMPLETELY before adding new one
        const existingMessages = messageContainer.querySelectorAll('.cart-message');
        existingMessages.forEach(msg => {
            if (msg.parentNode) {
                msg.parentNode.removeChild(msg);
            }
        });

        // Create message element
        const messageEl = document.createElement('div');
        messageEl.className = 'cart-message cart-' + type;
        messageEl.textContent = message;

        // Add to dedicated message container
        messageContainer.appendChild(messageEl);

        // Animate in with a slight delay
        setTimeout(function () {
            messageEl.style.opacity = '1';
            messageEl.style.transform = 'translateY(0)';
        }, 10);

        // Remove after 2 seconds
        setTimeout(function () {
            messageEl.style.opacity = '0';
            messageEl.style.transform = 'translateY(-10px)';
            setTimeout(function () {
                if (messageEl.parentNode) {
                    messageEl.parentNode.removeChild(messageEl);
                }
            }, 500);
        }, 2000);
    }

    // Listen for cart updates from Shopify's cart system
    document.addEventListener('cart:updated', handleCartUpdateSuccess);
    document.addEventListener('cart:error', handleCartUpdateError);

    // Listen for custom cart events
    document.addEventListener('cart-drawer:open', function () {
        // Don't hide page scroll - let users scroll while drawer is open
    });

    document.addEventListener('cart-drawer:close', function () {
        // Don't need to restore scroll since we don't hide it
    });

    // Auto-focus first input in checkout forms
    const checkoutInputs = document.querySelectorAll('.field__input');
    if (checkoutInputs.length > 0 && document.body.classList.contains('template-checkout')) {
        checkoutInputs[0].focus();
    }

    // Smooth scroll to cart errors
    const cartErrors = document.querySelectorAll('.cart-error');
    if (cartErrors.length > 0) {
        cartErrors[0].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    }

    console.log('Custom cart interactions initialized');

    // Page navigation detection for cart persistence
    setupCartNavigationPersistence();

    // Fallback handling for when fetch fails
    window.addEventListener('online', function () {
        console.log('Connection restored - cart interactions ready');
    });

    window.addEventListener('offline', function () {
        console.log('Connection lost - cart operations will queue');
    });

    // Enhanced cart storage management
    function setupCartNavigationPersistence() {
        // Clean up expired cart data on page load
        cleanupExpiredCartData();

        // Listen for page visibility changes (when user navigates away/back)
        document.addEventListener('visibilitychange', function () {
            if (document.hidden) {
                // User is navigating away - update localStorage with current timestamp
                updateCartTimestamp();

                // Check if cart drawer is open
                const drawer = document.getElementById('custom-cart-drawer');
                if (drawer && drawer.classList.contains('active')) {
                    // Store that cart should be shown when they come back
                    sessionStorage.setItem('show-cart-after-navigation', 'true');
                    console.log('Stored cart drawer state for page navigation');
                }
            } else {
                // User is coming back - cart state will be restored by checkCartStatePersistence
                console.log('User returned to page, cart state will be restored if needed');
            }
        });

        // Listen for beforeunload to store cart state
        window.addEventListener('beforeunload', function () {
            const drawer = document.getElementById('custom-cart-drawer');
            if (drawer && drawer.classList.contains('active')) {
                sessionStorage.setItem('show-cart-after-navigation', 'true');
            }
        });

        // Listen for page show event (back/forward navigation)
        window.addEventListener('pageshow', function (event) {
            if (event.persisted) {
                // Page was restored from cache - ensure cart state is correct
                console.log('Page restored from cache, checking cart state...');
                setTimeout(() => {
                    checkCartStatePersistence();
                }, 100);
            }
        });
    }
});

// Graceful fallback when JavaScript fails
window.addEventListener('error', function (error) {
    if (error.message && error.message.includes('cart')) {
        console.warn('Cart error detected, falling back to page reload method');
        // Could implement page-based cart updates here if needed
    }
});

// Cart cleanup and maintenance functions
function cleanupExpiredCartData() {
    try {
        const persistentCartData = localStorage.getItem('cart-persistent-data');
        if (persistentCartData) {
            const { timestamp } = JSON.parse(persistentCartData);
            const thirtyDaysAgo = Date.now() - 2592000000; // 30 days

            if (timestamp < thirtyDaysAgo) {
                localStorage.removeItem('cart-persistent-data');
                console.log('🗑️ Cleaned up expired cart data');
            }
        }
    } catch (error) {
        console.log('Error during cart cleanup:', error);
        localStorage.removeItem('cart-persistent-data');
    }
}

function updateCartTimestamp() {
    try {
        const persistentCartData = localStorage.getItem('cart-persistent-data');
        if (persistentCartData) {
            const cartData = JSON.parse(persistentCartData);
            cartData.lastUpdated = Date.now();
            localStorage.setItem('cart-persistent-data', JSON.stringify(cartData));
            console.log('📅 Updated cart timestamp');
        }
    } catch (error) {
        console.log('Error updating cart timestamp:', error);
    }
}

// Export functions for external use
window.CartEnhancements = {
    showMessage: function (message, type) {
        showCartMessage(message, type);
    },

    updateCartCount: function (count) {
        const cartBubbles = document.querySelectorAll('.cart-bubble');
        cartBubbles.forEach(function (bubble) {
            bubble.textContent = count.toString();
            bubble.style.display = count > 0 ? 'block' : 'none';
        });
    },

    // New functions for cart persistence management
    saveCartToLocalStorage: saveCartToLocalStorage,
    cleanupExpiredCartData: cleanupExpiredCartData,

    // Function to manually clear cart (for testing or admin purposes)
    clearPersistedCart: function () {
        localStorage.removeItem('cart-persistent-data');
        sessionStorage.removeItem('cart-count');
        console.log('🗑️ Manually cleared all persisted cart data');
    }
}; 